import { createContext, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { empresas as api } from "@plantus/api-client";
import { TODAS } from "@plantus/ui";
import type { Empresa } from "@plantus/api-client";

/**
 * As três pessoas jurídicas, vindas da API.
 *
 * <p>Substituiu a lista fixa que existia no frontend. Toda entidade regulada
 * tem empresa obrigatória e imutável (ADR-0005) — e uma lista de empresas que
 * vive só no cliente é a que fica desatualizada sem ninguém notar.
 *
 * <p>A empresa escolhida fica em memória, não em `localStorage`: a próxima
 * sessão começa do zero, e escolher a empresa errada por causa de uma
 * preferência esquecida é o tipo de erro que produz documento com o CNPJ
 * errado.
 *
 * <h2>Três escolhas possíveis, e o tipo diz quais</h2>
 *
 * <ul>
 *   <li><b>nada escolhido</b> (`escolha === null`) — estado inicial com mais de
 *       uma empresa. Obrigar a escolha é o que impede o lote de nascer no CNPJ
 *       errado por inércia;</li>
 *   <li><b>uma empresa</b> — `atual` responde qual;</li>
 *   <li><b>o consolidado</b> (`escolha === TODAS`) — `atual` é `null`, e é por
 *       isso que ele não vale para receber material.</li>
 * </ul>
 *
 * <p>`atual` continuar `null` no consolidado <b>é o controle</b>, não um
 * descuido: tudo que exige um CNPJ — receber lote, cadastrar fornecedor,
 * imprimir etiqueta — lê `atual` e já recusa. Não se recebe material "para
 * todas as empresas".
 *
 * <p><b>Quais</b> empresas o consolidado abrange é decisão de cada tela, não
 * deste provedor: matérias-primas junta só as produtoras, fornecedores junta as
 * três. O provedor guarda a escolha; a tela sabe o que ela significa ali.
 *
 * <h2>Desde a #24, ele é a ÚNICA fonte</h2>
 *
 * <p>O painel de risco mantinha uma segunda lista, fixa, no próprio setor. Duas
 * fontes da verdade para as mesmas três pessoas jurídicas divergem <b>sem erro
 * nenhum</b>: a tela continua desenhando, com o dado velho — e numa delas está o
 * CNPJ que identifica o detentor de cada documento regulado. A lista fixa saiu, e
 * há varredura que reprova a próxima (`fonteUnica.teste.ts`).
 */
interface EstadoDeEmpresas {
  lista: Empresa[];
  carregando: boolean;
  /**
   * A consulta falhou.
   *
   * <p>Existe porque <b>lista vazia por falha</b> e <b>lista vazia por ausência</b>
   * não são a mesma coisa, e uma tela que não as distingue mente por omissão:
   * ela desenha um seletor sem opção nenhuma, com ar de tela pronta. São três
   * empresas e elas nascem na migration — vazio aqui é sempre defeito, e dizê-lo
   * é a diferença entre uma tela quebrada e uma tela que não avisou.
   */
  erro: boolean;
  /** O identificador escolhido, {@link TODAS} ou `null` se ninguém escolheu. */
  escolha: string | null;
  /** A empresa em que se está trabalhando. `null` também no consolidado. */
  atual: Empresa | null;
  /** Se a escolha é a visão consolidada. */
  consolidado: boolean;
  escolher(id: string): void;
}

const Contexto = createContext<EstadoDeEmpresas>({
  lista: [],
  carregando: true,
  erro: false,
  escolha: null,
  atual: null,
  consolidado: false,
  escolher: () => undefined,
});

export function ProvedorDeEmpresas({ children }: { children: ReactNode }) {
  const [lista, definirLista] = useState<Empresa[]>([]);
  const [carregando, definirCarregando] = useState(true);
  const [erro, definirErro] = useState(false);
  const [escolha, definirEscolha] = useState<string | null>(null);

  useEffect(() => {
    let ativo = true;
    api
      .listar()
      .then((empresas) => {
        if (!ativo) return;
        definirLista(empresas);
        // Não escolhemos uma por padrão quando há mais de uma: obrigar a
        // escolha é o que impede o lote de nascer no CNPJ errado por inércia.
        //
        // E não escolhemos o CONSOLIDADO por padrão, apesar de ele agora
        // existir: a primeira tela que se abre é a de conferir material, e
        // abrir já misturando dois CNPJs seria começar pela leitura que exige
        // mais atenção. Consolidado é uma escolha, como as outras.
        if (empresas.length === 1) definirEscolha(empresas[0]!.id);
      })
      // Engolir a falha em silêncio era o que fazia a tela ficar com um seletor
      // vazio sem nada dizendo por quê. Quem consome decide o que mostrar; o
      // provedor só se recusa a fingir que respondeu.
      .catch(() => {
        if (ativo) definirErro(true);
      })
      .finally(() => {
        if (ativo) definirCarregando(false);
      });
    return () => {
      ativo = false;
    };
  }, []);

  const consolidado = escolha === TODAS;
  const atual = consolidado ? null : (lista.find((e) => e.id === escolha) ?? null);

  return (
    <Contexto.Provider
      value={{ lista, carregando, erro, escolha, atual, consolidado, escolher: definirEscolha }}
    >
      {children}
    </Contexto.Provider>
  );
}

export function usarEmpresas(): EstadoDeEmpresas {
  return useContext(Contexto);
}
